import { useCookies } from 'react-cookie';
export interface GridManagerFieldItem {
  id: string;
  column: string;
  type: string;
  gridAvailability: boolean;
  visibility: boolean;
  fieldName: string;
  order: number;
}

export interface GridColCommon {
    id: string;
    [key: string]: any;
  }

export const sortFn = (a: any, b: any) => {
  return a.order - b.order;
};


 export const isISODateString = (value: any): boolean => {
    // Regex pattern for ISO 8601 date format
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
    return typeof value === "string" && isoDatePattern.test(value);
  };

 export const isISODateTimeLike = (value: any): boolean => {
    return (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/.test(value)
    );
  };

 export function formatDate(datestring: string) {
    const currentDate = new Date(datestring);
    const day = currentDate.getDate().toString().padStart(2, "0");
    const monthName = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthName[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    return `\t${day}/${month}/${year}`;
  }

 export function formatDateTime(datestring: string) {
    const currentDate = new Date(datestring);
    if (Number.isNaN(currentDate.getTime())) return datestring;
    const day = currentDate.getUTCDate().toString().padStart(2, "0");
    const monthName = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthName[currentDate.getUTCMonth()];
    const year = currentDate.getUTCFullYear();
    const hours = currentDate.getUTCHours().toString().padStart(2, "0");
    const minutes = currentDate.getUTCMinutes().toString().padStart(2, "0");
    const seconds = currentDate.getUTCSeconds().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} UTC`;
  }

  /** ISO timestamps become `dd/MMM/yyyy hh:mm:ss UTC`. Values already in DD/MMM/YYYY are left unchanged. */
  export function formatAckStatusDateDisplay(value: unknown) {
    if (isISODateTimeLike(value)) return formatDateTime(value as string);
    return value;
  }

  const UTC_DATETIME_DISPLAY_FIELDS = [
    "ackStatusDate",
    "authorizationRenewalDate",
    "infoDate",
    "attachmentVersionDate",
  ];

  export function isUtcDateTimeDisplayField(fieldName: unknown): boolean {
    return typeof fieldName === "string" && UTC_DATETIME_DISPLAY_FIELDS.includes(fieldName);
  }

  /** xEVMPD main form only (display/{id}); not used on display-all list grid. */
  export function isXevmpdFormDateTimeDisplayField(fieldName: unknown): boolean {
    return fieldName === "statusDate" || fieldName === "versionDate";
  }

  export function formatXevmpdFormDateFieldsForDisplay(
    data: Record<string, any> | null | undefined
  ): Record<string, any> {
    if (data == null) {
      return {};
    }
    if (typeof data !== "object") {
      return data;
    }
    const result: Record<string, any> = { ...data };
    (["statusDate", "versionDate"] as const).forEach((key) => {
      if (
        Object.prototype.hasOwnProperty.call(result, key) &&
        result[key] != null &&
        result[key] !== ""
      ) {
        result[key] = formatAckStatusDateDisplay(result[key]);
      }
    });
    return result;
  }

  /** Format known UTC datetime fields for UI display only; leaves other keys unchanged. */
  export function formatUtcDateTimeFieldsForDisplay(
    data: Record<string, any> | null | undefined
  ): Record<string, any> {
    if (data == null) {
      return {};
    }
    if (typeof data !== "object") {
      return data;
    }
    const result: Record<string, any> = { ...data };
    UTC_DATETIME_DISPLAY_FIELDS.forEach((key) => {
      if (
        Object.prototype.hasOwnProperty.call(result, key) &&
        result[key] != null &&
        result[key] !== ""
      ) {
        result[key] = formatAckStatusDateDisplay(result[key]);
      }
    });
    return result;
  }

  const ACK_STATUS_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  /** Parse ISO timestamps and displayed ack status dates to a UTC epoch for sorting. */
  export function parseAckStatusDateValue(value: unknown): number | null {
    if (value == null || value === "") return null;
    if (isISODateTimeLike(value)) {
      const time = new Date(value as string).getTime();
      return Number.isNaN(time) ? null : time;
    }
    if (typeof value !== "string") return null;
    const match = value
      .trim()
      .match(
        /^(\d{1,2})\/([A-Za-z]{3})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2})(?:\s*UTC)?)?$/
      );
    if (!match) return null;
    const monthName =
      match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase();
    const month = ACK_STATUS_MONTHS.indexOf(monthName);
    if (month < 0) return null;
    const time = Date.UTC(
      Number(match[3]),
      month,
      Number(match[1]),
      match[4] != null ? Number(match[4]) : 0,
      match[5] != null ? Number(match[5]) : 0,
      match[6] != null ? Number(match[6]) : 0
    );
    return Number.isNaN(time) ? null : time;
  }

  export function compareAckStatusDate(a: unknown, b: unknown): number {
    const left = parseAckStatusDateValue(a);
    const right = parseAckStatusDateValue(b);
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    return left - right;
  }

  export function utcDateTimeGridValueGetter(fieldName: string) {
    return (params: any) => {
      const raw = params?.row?.[fieldName] ?? params?.value;
      const display = formatAckStatusDateDisplay(raw);
      return display == null || display === "" ? "" : String(display);
    };
  }

  export function utcDateTimeGridSortComparator(v1: unknown, v2: unknown) {
    return compareAckStatusDate(v1, v2);
  }

  /** Client-side grid filter operators (contains / equals) on formatted display text. */
  export function mapUtcDateTimeStringFilterOperators<T extends { value: string }>(
    operators: T[],
    fieldName: string
  ): T[] {
    return operators
      .filter((op) => op.value === "contains" || op.value === "equals")
      .map((operator) => ({
        ...operator,
        getApplyFilterFn: (filterItem: { value?: unknown }) => {
          if (
            filterItem.value == null ||
            (typeof filterItem.value === "string" && filterItem.value === "")
          ) {
            return null;
          }
          const filterValue = String(filterItem.value).toLowerCase();
          const op = operator.value;
          return (params: any) => {
            const raw = params?.row?.[fieldName];
            const display = formatAckStatusDateDisplay(raw);
            const cellText =
              display == null || display === ""
                ? ""
                : String(display).toLowerCase();
            if (op === "contains") {
              return cellText.includes(filterValue);
            }
            return cellText === filterValue;
          };
        },
      }));
  }

  export function utcDateTimeGridColumnBase(fieldName: string) {
    return {
      type: "string" as const,
      sortable: true,
      filterable: true,
      valueGetter: utcDateTimeGridValueGetter(fieldName),
      sortComparator: utcDateTimeGridSortComparator,
    };
  }

  /** Expand server filter values so ISO and display formats can both match backend search. */
  export function expandUtcDateTimeFilterValues(
    fieldName: string,
    value: string
  ): string[] {
    if (!isUtcDateTimeDisplayField(fieldName)) {
      return [value];
    }
    const trimmed = value.trim();
    const values = new Set<string>([trimmed]);
    if (isISODateTimeLike(trimmed)) {
      const display = formatAckStatusDateDisplay(trimmed);
      if (typeof display === "string" && display) {
        values.add(display);
      }
    } else {
      const epoch = parseAckStatusDateValue(trimmed);
      if (epoch != null) {
        values.add(new Date(epoch).toISOString());
      }
    }
    return Array.from(values);
  }


 export const handleMenuDataValue = (originName: string): string => {
      return originName
        .replace(/[\\/-]/g, "")
        .split(/\s+/)
        .map((word, index) =>
          index === 0
            ? word.charAt(0).toLowerCase() + word.slice(1)
            : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join("");
    };

  export const getAllIds = (tree: any, result: any) => {
  if (!Array.isArray(tree)) return [];
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    result.push(node.id);
    if (Array.isArray(node.children)) {
      getAllIds(node.children, result);
    }
  }
  return result;
}

export const canParseToJson = (str: any) => {
  if (typeof str == 'string') {
    try {
      let obj = JSON.parse(str);
      if (typeof obj == 'object' && obj) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
}



export function firstWordCase(parameter: string) {
  return parameter.slice(0, 1).toUpperCase() + parameter.slice(1).toLowerCase();
}



export const formName = "xEVMPD";
export const module = "xevmpd";

export const transformFormDataForReset = (
  formData: any, 
  formFields: any[], 
  relatedData?: any
) => {
  if (!formData) return formData;

  const transformedData = { ...formData };
  if (!Array.isArray(formFields)) return transformedData;

  formFields.forEach((field: any) => {
    // Handle attachment field
    if (field.field === "attachment" && !formData[field.field]) {
      return;
    }
    
    // Handle radio fields - take everything from relatedData
    if (field.type === "radio" && relatedData?.[field.field] !== undefined) {
      const relatedFieldValue = relatedData[field.field];
      
      // For radio, we want the value (not label-value object)
      // Radio fields typically have string/number values
      transformedData[field.field] = relatedFieldValue;
      return;
    }
    
    // Handle select fields - label from cellData, value from relatedData
    if (field.type === "select" && formData[field.field] !== undefined) {
      const fieldValue = formData[field.field];
      const relatedFieldValue = relatedData?.[field.field];

      // Skip if already null/undefined
      if (fieldValue === null || fieldValue === undefined) {
        return;
      }

      // Handle multi-select fields
      if (field.multiple === true) {
        // Initialize as empty array if value is falsy
        if (!fieldValue) {
          transformedData[field.field] = [];
          return;
        }

        // If it's already an array of objects with value property, check if we need to update
        if (Array.isArray(fieldValue)) {
          const isProperlyFormatted = fieldValue.length === 0 || 
            fieldValue.every(
              (item: any) => 
                item && 
                typeof item === "object" && 
                "value" in item && 
                "label" in item
            );
          
          if (isProperlyFormatted && relatedFieldValue !== undefined) {
            // Update values from relatedData while keeping labels
            transformedData[field.field] = fieldValue.map((item: any, index: number) => {
              const newValue = Array.isArray(relatedFieldValue) 
                ? relatedFieldValue[index] 
                : relatedFieldValue;
              return {
                ...item,
                value: newValue !== undefined ? newValue : item.value,
              };
            });
            return;
          } else if (isProperlyFormatted) {
            // Already properly formatted, no related data
            return;
          }
          
          // Get related values (could be array or single value)
          let relatedValues: any[] = [];
          if (Array.isArray(relatedFieldValue)) {
            relatedValues = relatedFieldValue;
          } else if (relatedFieldValue !== undefined) {
            relatedValues = [relatedFieldValue];
          }
          
          // Transform array: use label from cellData, value from relatedData
          transformedData[field.field] = fieldValue.map((item: any, index: number) => {
            // Get value from relatedData if available, otherwise use item
            const value = relatedValues[index] !== undefined ? relatedValues[index] : item;
            return {
              label: String(item), // Label from cellData
              value: value, // Value from relatedData
            };
          });
        } 
        // Handle comma-separated string
        else if (typeof fieldValue === "string") {
          const labels = fieldValue
            .split(",")
            .map((v: string) => v.trim())
            .filter(Boolean);
          
          // Get related values
          let relatedValues: any[] = [];
          if (Array.isArray(relatedFieldValue)) {
            relatedValues = relatedFieldValue;
          } else if (typeof relatedFieldValue === "string") {
            relatedValues = relatedFieldValue.split(",").map(v => v.trim());
          } else if (relatedFieldValue !== undefined) {
            relatedValues = [relatedFieldValue];
          }
          
          transformedData[field.field] = labels.map((label: any, index: number) => ({
            label: String(label), // Label from cellData
            value: relatedValues[index] !== undefined ? relatedValues[index] : label, // Value from relatedData
          }));
        }
        // Handle single value (wrap in array)
        else {
          transformedData[field.field] = [{
            label: String(fieldValue), // Label from cellData
            value: relatedFieldValue !== undefined ? relatedFieldValue : fieldValue, // Value from relatedData
          }];
        }
        return;
      }
      
      // Handle single select fields
      if (
        fieldValue &&
        typeof fieldValue === "object" &&
        "value" in fieldValue &&
        "label" in fieldValue
      ) {
        // Already properly formatted, but we might want to update value from relatedData
        if (relatedFieldValue !== undefined) {
          transformedData[field.field] = {
            ...fieldValue, // Keep existing object properties (especially label)
            value: relatedFieldValue, // Update value from relatedData
          };
        }
        return;
      }

      // Transform string/number to object with merged data
      if (typeof fieldValue === "string" || typeof fieldValue === "number") {
        transformedData[field.field] = {
          label: String(fieldValue), // Label from cellData
          value: relatedFieldValue !== undefined ? relatedFieldValue : fieldValue, // Value from relatedData
        };
      }
    }
  });

  return transformedData;
};


export function useAppCookies() {
  const [cookies] = useCookies(['USER_TENANT_ID', 'DOMAIN','ACCESS_TOKEN', 'USER_NAME', 'USER_ID']);
  const tenantID = cookies.USER_TENANT_ID;
  const userName = cookies.USER_NAME ?? 'ABC';
  const userID = cookies.USER_ID;
  const token = cookies.ACCESS_TOKEN;
  const domain = "All";
  return { tenantID, userName, cookies, userID, token,domain};
}

export const getTruncatedName = (name: string, limit = 30) =>
  name?.length > limit ? `${name.substring(0, limit)}...` : name;


export const toArray = (value: any) => {
  return Array.isArray(value) ? value : [value];
};

export const extractVersion = (versionString : any) => {
  if (!versionString) {
    return { major: 0, minor: 0 };
  }
  
  // Remove 'V' or 'v' prefix and split by dot
  const parts = versionString.replace(/^[Vv]/, '').split('.');
  
  return {
    major: parseInt(parts[0]) || 0,
    minor: parseInt(parts[1]) || 0
  };
};